import { Component, Input } from '@angular/core';
import { SgTextVariant } from '../sg-text/sg-text.component';

const CLASS_PREFIX = 'sg-avatar-';
const TEXT_VARIANT_MAP: { [key: string]: SgTextVariant } = {
  small: 'sg-label-small-regular',
  medium: 'sg-label-medium-regular',
  large: 'sg-label-large-regular',
  xlarge: 'sg-heading-small-bold',
};

/** Public color names callers can pass via the `color` input. */
export type SgAvatarColor = 'sky-blue' | 'turquoise' | 'magenta' | 'dusk' | 'purple';

const COLOR_VAR_BY_NAME: Record<SgAvatarColor, string> = {
  'sky-blue': '--sg-content-accent-skyblue-primary',
  turquoise: '--sg-content-accent-turquoise-primary',
  magenta: '--sg-content-accent-magenta-primary',
  dusk: '--sg-content-accent-dusk-primary',
  purple: '--sg-content-accent-purple-primary',
};

const HASH_COLOR_VARS = Object.values(COLOR_VAR_BY_NAME);

@Component({
  selector: 'sg-avatar',
  templateUrl: './sg-avatar.component.html',
  styleUrls: ['./sg-avatar.component.scss'],
  standalone: false,
})
export class SgAvatarComponent {
  @Input() firstName = '';
  @Input() lastName = '';
  @Input() size: 'small' | 'medium' | 'large' | 'xlarge' = 'medium';
  @Input() showTooltip = true;
  /**
   * Optional fixed background color. When provided, overrides the
   * auto-hashing of `firstName + lastName`. Pass one of the design-system
   * accent names so the avatar stays on-palette.
   */
  @Input() color?: SgAvatarColor;

  get initials(): string {
    const firstInitial = this.firstName.charAt(0).toUpperCase();
    const lastInitial = this.lastName.charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  }

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  get avatarSizeClass(): string {
    return CLASS_PREFIX + this.size;
  }

  get textVariant(): SgTextVariant {
    return TEXT_VARIANT_MAP[this.size];
  }

  getAvatarColor(): string {
    // Explicit override wins over the name-hash fallback.
    if (this.color) {
      return COLOR_VAR_BY_NAME[this.color];
    }
    const hash = this.hashCode(this.fullName);
    const number = (Math.abs(hash) % HASH_COLOR_VARS.length) + 1;
    return HASH_COLOR_VARS[number - 1];
  }

  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0; // Convert to 32bit integer
    }
    return hash;
  }
}
